import React, { useEffect, useState } from "react";
import { Modal, Typography, Row, Col, Divider, Card, Image, Alert } from "antd";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import useFeedbackFormService from "../../../../services/feedbackFormService";
import { PictureOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const DetailsModal = ({ visible, onClose, feedback }) => {
  const { language } = useLanguage();
  const [formular, setFormular] = useState({});
  const { getFeedbackFormById } = useFeedbackFormService();
  const text = dashboardContent[language].home.detailsModal;
  const formId =
    typeof feedback?.form === "string" ? feedback?.form : feedback?.form?._id;

  useEffect(() => {
    if (!formId || !visible) return;
    getFeedbackFormById(formId).then(setFormular);
  }, [formId, visible]);

  if (!feedback) return null;

  const findSimilarQuestion = (questionText) => {
    if (!formular.questions || !questionText) return null;

    // Normalisiere den Text für besseren Vergleich
    const normalizeText = (text) =>
      String(text || "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s]/g, "");

    const normalizedSearch = normalizeText(questionText);

    // Suche nach exakter Übereinstimmung
    let match = formular.questions.find(
      (q) => normalizeText(q.questionText) === normalizedSearch
    );

    // Falls keine exakte Übereinstimmung, suche nach teilweiser Übereinstimmung
    if (!match) {
      match = formular.questions.find((q) => {
        const normalized = normalizeText(q.questionText);
        return (
          normalized.includes(normalizedSearch) ||
          normalizedSearch.includes(normalized)
        );
      });
    }

    return match;
  };

  const responseMap = feedback.responses?.reduce((acc, curr) => {
    const qId =
      curr.questionId && typeof curr.questionId === "object"
        ? curr.questionId._id
        : curr.questionId;
    acc[String(qId)] = curr;
    return acc;
  }, {});

  // Neue Funktion: Finde verwaiste Antworten (Antworten ohne passende Frage im aktuellen Formular)
  const getOrphanedResponses = () => {
    console.log(feedback.responses);
    console.log(formular.questions);
    if (!feedback.responses || !formular.questions) return [];

    const currentQuestionIds = new Set(
      formular.questions.map((q) => String(q._id))
    );

    return feedback.responses.filter((response) => {
      const responseQuestionId =
        response.questionId && typeof response.questionId === "object"
          ? String(response.questionId._id)
          : String(response.questionId);

      // Prüfe ob die Frage nicht im aktuellen Formular ist
      const isOrphaned = !currentQuestionIds.has(responseQuestionId);

      // Prüfe ob eine echte Antwort vorhanden ist (gleiche Logik wie bei aktuellen Fragen)
      const hasValidAnswer =
        response &&
        response.answer &&
        String(response.answer).trim() !== "" &&
        String(response.answer).trim() !== "N/A" &&
        String(response.answer).trim() !== "n/a" &&
        String(response.answer).trim() !== "null" &&
        String(response.answer).trim() !== "undefined";

      return isOrphaned && hasValidAnswer;
    });
  };

  // Funktion zum Rendern einer verwaisten Antwort
  const renderOrphanedResponse = (response, index) => {
    // QuestionText kann direkt auf response stehen oder in questionId Object
    const questionText =
      response.questionText ||
      (response.questionId && typeof response.questionId === "object"
        ? response.questionId.questionText
        : null) ||
      "Unbekannte Frage";
    // Gesamtbewertung auch bei verwaisten Antworten ausschließen
    if (questionText === "Gesamtbewertung") return null;

    // Versuche ähnliche Frage im aktuellen Formular zu finden
    const similarQuestion = findSimilarQuestion(questionText);

    // QuestionType kann direkt auf response stehen oder in questionId Object
    let questionType =
      response.questionType ||
      (response.questionId && typeof response.questionId === "object"
        ? response.questionId.type
        : null) ||
      "text";

    // Wenn ähnliche Frage gefunden wurde, nutze deren Typ und Optionen
    let questionOptions = null;
    let questionStars = 5;

    if (similarQuestion) {
      questionType = similarQuestion.type;
      questionOptions = similarQuestion.options;
      questionStars = similarQuestion.stars || 5;
    }

    return (
      <Card
        key={`orphaned-${index}`}
        bordered={false}
        style={{
          borderLeft: `4px solid ${accentColor}`,
          borderRadius: 4,
          background: "#fafafa",
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Text strong style={{ fontSize: 16 }}>
          {questionText}
        </Text>
        <div className="mt-2">
          {renderAnswerBasedOnType(
            response,
            questionType,
            questionOptions,
            questionStars
          )}
        </div>
      </Card>
    );
  };

  // Hilfsfunktion zum Rendern der Antwort basierend auf dem Typ
  // Hilfsfunktion zum Rendern der Antwort basierend auf dem Typ
  const renderAnswerBasedOnType = (
    response,
    questionType,
    questionOptions = null,
    questionStars = 5
  ) => {
    const answer = response.answer;

    switch (questionType) {
      case "yesno":
        const [choice, comment] = String(answer || "").split(/,\s*(.+)/);
        return (
          <div>
            <Text>
              <strong>{choice}</strong>
            </Text>
            {comment && (
              <div className="mt-1">
                <Text type="secondary">{comment}</Text>
              </div>
            )}
          </div>
        );

      case "star-rating":
        const rating = parseInt(answer) || 0;
        const maxStars = questionStars || 5;
        return (
          <div className="flex items-center space-x-1">
            {[...Array(maxStars)].map((_, i) => (
              <svg
                key={i}
                xmlns="http://www.w3.org/2000/svg"
                fill={i < rating ? "#fa8c16" : "none"}
                viewBox="0 0 24 24"
                stroke="#fa8c16"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                />
              </svg>
            ))}
            <Text style={{ marginLeft: 8 }}>
              ({rating}/{maxStars})
            </Text>
          </div>
        );

      case "multiple-choice":
        // Wenn Optionen vorhanden (von ähnlicher Frage), zeige Checkboxen
        if (questionOptions && questionOptions.length > 0) {
          const selectedAnswers = Array.isArray(answer) ? answer : [answer];
          return (
            <div className="space-y-1">
              {questionOptions.map((option, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    disabled
                    checked={selectedAnswers.includes(option)}
                    className="h-4 w-4 text-orange-500"
                  />
                  <label className="text-gray-800">{option}</label>
                </div>
              ))}
            </div>
          );
        }
        // Fallback: Nur Bullets wenn keine Optionen
        const answers = Array.isArray(answer) ? answer : [answer];
        return (
          <div className="space-y-1">
            {answers.map((ans, idx) => (
              <div key={idx}>
                <Text>• {ans}</Text>
              </div>
            ))}
          </div>
        );

      case "single-choice":
        // Wenn Optionen vorhanden (von ähnlicher Frage), zeige Radio-Buttons
        if (questionOptions && questionOptions.length > 0) {
          return (
            <div className="space-y-1">
              {questionOptions.map((option, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    disabled
                    checked={answer === option}
                    className="h-4 w-4 text-orange-500"
                  />
                  <label className="text-gray-800">{option}</label>
                </div>
              ))}
            </div>
          );
        }
        // Fallback: Nur Text
        return <Text>• {answer}</Text>;

      default: // text und andere
        return <Text>{answer || text.notAvailable}</Text>;
    }
  };

  const renderQuestionViewOnly = (question, index) => {
    const response = responseMap?.[String(question._id)];

    // Gesamtbewertung ausschließen - wird bereits oben in den Karten angezeigt
    if (question.questionText === "Gesamtbewertung") return null;

    // Wenn keine gültige Antwort vorhanden ist, nichts rendern
    const hasValidAnswer =
      response &&
      response.answer &&
      String(response.answer).trim() !== "" &&
      String(response.answer).trim() !== "N/A" &&
      String(response.answer).trim() !== "n/a" &&
      String(response.answer).trim() !== "null" &&
      String(response.answer).trim() !== "undefined";

    if (!hasValidAnswer) return null;

    const answer = Array.isArray(response?.answer)
      ? response.answer
      : response?.answer !== undefined
      ? [response.answer]
      : [];

    switch (question.type) {
      case "yesno":
        const raw = response?.answer || "";
        const [choice, comment] = raw.split(/,\s*(.+)/);
        return (
          <Card
            key={index}
            bordered={false}
            style={{
              borderLeft: `4px solid ${accentColor}`,
              borderRadius: 4,
              background: "#fafafa",
            }}
            bodyStyle={{ padding: 16 }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {question.questionText}
            </Text>
            <div className="mt-2">
              <Text>
                <strong>{choice}</strong>
              </Text>
              {comment && (
                <div className="mt-1">
                  <Text type="secondary">{comment}</Text>
                </div>
              )}
            </div>
          </Card>
        );

      case "multiple-choice":
        return (
          <Card
            key={index}
            bordered={false}
            style={{
              borderLeft: `4px solid ${accentColor}`,
              borderRadius: 4,
              background: "#fafafa",
            }}
            bodyStyle={{ padding: 16 }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {question.questionText}
            </Text>
            <div className="mt-2 space-y-1">
              {question.options.map((option, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    disabled
                    checked={answer.includes(option)}
                    className="h-4 w-4 text-orange-500"
                  />
                  <label className="text-gray-800">{option}</label>
                </div>
              ))}
            </div>
          </Card>
        );

      case "single-choice":
        return (
          <Card
            key={index}
            bordered={false}
            style={{
              borderLeft: `4px solid ${accentColor}`,
              borderRadius: 4,
              background: "#fafafa",
            }}
            bodyStyle={{ padding: 16 }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {question.questionText}
            </Text>
            <div className="mt-2 space-y-1">
              {question.options.map((option, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`readonly-${index}`}
                    disabled
                    checked={response?.answer === option}
                    className="h-4 w-4 text-orange-500"
                  />
                  <label className="text-gray-800">{option}</label>
                </div>
              ))}
            </div>
          </Card>
        );

      case "text":
        return (
          <Card
            key={index}
            bordered={false}
            style={{
              borderLeft: `4px solid ${accentColor}`,
              borderRadius: 4,
              background: "#fafafa",
            }}
            bodyStyle={{ padding: 16 }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {question.questionText}
            </Text>
            <div className="mt-2">
              <Text>{response?.answer}</Text>
            </div>
          </Card>
        );

      case "star-rating":
        return (
          <Card
            key={index}
            bordered={false}
            style={{
              borderLeft: `4px solid ${accentColor}`,
              borderRadius: 4,
              background: "#fafafa",
            }}
            bodyStyle={{ padding: 16 }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {question.questionText}
            </Text>
            <div className="mt-2 flex items-center space-x-1">
              {[...Array(question.stars || 5)].map((_, i) => (
                <svg
                  key={i}
                  xmlns="http://www.w3.org/2000/svg"
                  fill={i < response?.answer ? "#fa8c16" : "none"}
                  viewBox="0 0 24 24"
                  stroke="#fa8c16"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                  />
                </svg>
              ))}
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  // Render Images Section
  const renderImagesSection = () => {
    if (!feedback.images || feedback.images.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: 24 }}>
        <Card
          bordered={false}
          style={{
            borderLeft: `4px solid ${accentColor}`,
            borderRadius: 4,
            background: "#fafafa",
          }}
          bodyStyle={{ padding: 16 }}
        >
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 16 }}
          >
            <PictureOutlined
              style={{
                color: accentColor,
                fontSize: 18,
                marginRight: 8,
              }}
            />
            <Text strong style={{ fontSize: 16 }}>
              {language === "DE" ? "Angehängte Bilder" : "Attached Images"}
              <span
                style={{ color: "#999", fontWeight: "normal", marginLeft: 8 }}
              >
                ({feedback.images.length})
              </span>
            </Text>
          </div>

          <Row gutter={[12, 12]}>
            {feedback.images.map((imageUrl, index) => (
              <Col key={index} xs={12} sm={8} md={6}>
                <div
                  style={{
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "2px solid #f0f0f0",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = accentColor;
                    e.target.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = "#f0f0f0";
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  <Image
                    src={imageUrl}
                    alt={`Feedback Bild ${index + 1}`}
                    style={{
                      width: "100%",
                      height: 120,
                      objectFit: "cover",
                    }}
                    preview={{
                      mask: (
                        <div
                          style={{
                            color: "#fff",
                            textAlign: "center",
                            fontSize: 12,
                          }}
                        >
                          <PictureOutlined
                            style={{
                              fontSize: 16,
                              display: "block",
                              marginBottom: 4,
                            }}
                          />
                          {language === "DE" ? "Vergrößern" : "Preview"}
                        </div>
                      ),
                    }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAWIWdkMEowkAQhl+IamkJ8iKh3RYs+kL9AuEuokhqF9vIbLNs2U2WdcHbxQcQdKgIXwBv4n3gF+gtcF/B0+fCDJOe9Q3eLzJmJoDBzpAeOgT4e4zN5e5QLwuC4GPMG2MZ8Ec7mjK7rq+VwUiOHEfFpXJCQNgZP6m4x9Phs8V38j8GqnXV5xVRUF5UgApKgZp7b9e9KKP4Rhr7rV+fH"
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      </div>
    );
  };

  const items = [
    {
      key: "form",
      label: text.formLabel,
      value: feedback.category || text.unknownForm,
    },
    {
      key: "from",
      label: text.fromLabel,
      value: feedback.from || text.unknownUser,
    },
    {
      key: "rating",
      label: text.ratingLabel,
      value: feedback.rating || text.notAvailable,
    },
  ];
  const accentColor = "#fa8c16";

  // Hole verwaiste Antworten
  const orphanedResponses = getOrphanedResponses();

  return (
    <Modal
      title={<Title level={4}>{text.title}</Title>}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="95%"
      style={{ maxWidth: 800 }}
      bodyStyle={{
        padding: 24,
        maxHeight: "70vh",
        overflowY: "auto",
        background: "#fff",
      }}
    >
      {/* Header Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {items.slice(0, 2).map((item) => (
          <Col key={item.key} xs={24} sm={12}>
            <Card
              style={{
                background: accentColor,
                color: "#fff",
                borderRadius: 6,
                textAlign: "center",
              }}
              bodyStyle={{
                padding: 24,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Title level={4} style={{ color: "#fff", margin: 0 }}>
                {item.label}
              </Title>
              <Title
                level={5}
                style={{
                  color: "#fff",
                  marginTop: 8,
                  maxWidth: item.key === "from" ? 160 : "auto",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
                ellipsis={item.key === "from"}
              >
                {item.value}
              </Title>
            </Card>
          </Col>
        ))}
      </Row>
      <Row justify="center" style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card
            style={{
              background: accentColor,
              color: "#fff",
              borderRadius: 6,
              textAlign: "center",
            }}
            bodyStyle={{
              padding: 24,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Title level={5} style={{ color: "#fff", margin: 0 }}>
              {items[2].label}
            </Title>
            <Title
              level={4}
              style={{ color: "#fff", marginTop: 8, fontWeight: "bold" }}
            >
              {items[2].value}
            </Title>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Images Section */}
      {renderImagesSection()}

      {/* Aktuelle Fragen */}
      <Row gutter={[16, 16]}>
        {formular.questions?.map((question, index) => (
          <Col span={24} key={question._id}>
            {renderQuestionViewOnly(question, index)}
          </Col>
        ))}
      </Row>

      {/* Verwaiste Antworten - einfach dazwischen gemischt */}
      {orphanedResponses.length > 0 &&
        orphanedResponses.map((response, index) => (
          <Col span={24} key={`orphaned-${index}`}>
            {renderOrphanedResponse(response, index)}
          </Col>
        ))}
    </Modal>
  );
};

export default DetailsModal;
